import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuoteLeft,
  faBuilding,
  faTruck,
  faShip,
  faPlane,
  faChartLine,
  faClock,
  faUsers,
  faAward,
  faExclamationTriangle,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import DemoModal from '../components/DemoModal';
import PurchaseModal from '../components/PurchaseModal';

gsap.registerPlugin(ScrollTrigger);

const Cases = ({ onDemoClick, onPurchaseClick }) => {
  const pageRef = useRef(null);
  const [selectedCase, setSelectedCase] = useState(0);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const customerCases = [
    {
      id: 1,
      company: '安能物流（杭州网点）',
      industry: '快运网络',
      icon: faTruck,
      logo: '/api/placeholder/120/60',
      description: '安能物流杭州地区加盟网点',
      challenge: '网点财务数据分散，手工记账效率低，成本核算不准确',
      solution: '部署云帐天下智能财务系统，实现订单数据自动化处理和精准成本核算',
      results: [
        { metric: '记账效率', improvement: '提升300%', icon: faChartLine },
        { metric: '数据准确率', improvement: '达到99.5%', icon: faAward },
        { metric: '财务人员', improvement: '减少50%', icon: faUsers },
        { metric: '月结时间', improvement: '缩短80%', icon: faClock }
      ],
      testimonial: {
        content: '云帐天下系统让我们网点的财务管理变得简单高效，再也不用担心账目混乱了。',
        author: '张站长',
        position: '网点负责人'
      },
      tags: ['加盟网点', '快运业务', '自动化']
    },
    {
      id: 2,
      company: '中通快运（上海网点）',
      industry: '快运物流',
      icon: faPlane,
      logo: '/api/placeholder/120/60',
      description: '中通快运上海地区服务网点',
      challenge: '快运业务量大，计费复杂，财务核算工作量繁重',
      solution: '采用智能凭证生成和多维度报表分析功能，优化财务流程',
      results: [
        { metric: '凭证生成', improvement: '自动化95%', icon: faChartLine },
        { metric: '核算准确率', improvement: '提升至99.8%', icon: faAward },
        { metric: '工作效率', improvement: '提升200%', icon: faUsers },
        { metric: '对账时间', improvement: '缩短90%', icon: faClock }
      ],
      testimonial: {
        content: '系统的智能化程度超出预期，特别是自动凭证生成功能，大大减轻了我们的工作负担。',
        author: '李经理',
        position: '财务主管'
      },
      tags: ['快运网点', '智能化', '高效率']
    },
    {
      id: 3,
      company: '韵达快运（深圳网点）',
      industry: '快递快运',
      icon: faBuilding,
      logo: '/api/placeholder/120/60',
      description: '韵达快运深圳地区运营网点',
      challenge: '业务类型多样，收费标准复杂，财务管理难度大',
      solution: '部署多业务类型财务管理模块，实现精细化成本控制',
      results: [
        { metric: '成本控制', improvement: '精准度提升85%', icon: faChartLine },
        { metric: '利润分析', improvement: '实时可视化', icon: faAward },
        { metric: '决策效率', improvement: '提升150%', icon: faUsers },
        { metric: '运营成本', improvement: '降低25%', icon: faClock }
      ],
      testimonial: {
        content: '现在我们能够实时掌握每个业务类型的盈利情况，经营决策更加科学准确。',
        author: '王总',
        position: '网点负责人'
      },
      tags: ['快运网点', '精细化管理', '成本控制']
    },
    {
      id: 4,
      company: '顺心捷达（广州网点）',
      industry: '快运物流',
      icon: faShip,
      logo: '/api/placeholder/120/60',
      description: '顺心捷达广州地区服务网点',
      challenge: '网点规模扩大，财务数据量激增，传统管理方式难以应对',
      solution: '建立智能化财务数据处理系统，实现规模化运营管理',
      results: [
        { metric: '数据处理', improvement: '速度提升500%', icon: faChartLine },
        { metric: '管理效率', improvement: '提升180%', icon: faAward },
        { metric: '错误率', improvement: '降低95%', icon: faUsers },
        { metric: '扩张支持', improvement: '无缝对接', icon: faClock }
      ],
      testimonial: {
        content: '云帐天下系统完美支持了我们网点的快速发展，让财务管理跟上了业务扩张的步伐。',
        author: '陈经理',
        position: '运营经理'
      },
      tags: ['规模化运营', '数据处理', '快速发展']
    },
    {
      id: 5,
      company: '百世快运（北京网点）',
      industry: '快运物流',
      icon: faTruck,
      logo: '/api/placeholder/120/60',
      description: '百世快运北京地区运营网点',
      challenge: '业务流程复杂，多种收费模式并存，财务核算困难',
      solution: '部署多模式计费系统，实现复杂业务场景的自动化处理',
      results: [
        { metric: '计费准确率', improvement: '提升至99.9%', icon: faChartLine },
        { metric: '处理速度', improvement: '提升400%', icon: faAward },
        { metric: '人工干预', improvement: '减少80%', icon: faUsers },
        { metric: '客户满意度', improvement: '提升35%', icon: faClock }
      ],
      testimonial: {
        content: '系统完美解决了我们多种计费模式的复杂问题，现在财务工作变得井井有条。',
        author: '刘站长',
        position: '网点站长'
      },
      tags: ['多模式计费', '复杂业务', '自动化']
    },
    {
      id: 6,
      company: '壹米滴答（成都网点）',
      industry: '零担快运',
      icon: faBuilding,
      logo: '/api/placeholder/120/60',
      description: '壹米滴答成都地区加盟网点',
      challenge: '零担业务货物种类繁多，计重计方复杂，成本分摊困难',
      solution: '采用智能计重计方系统，实现精准成本分摊和利润分析',
      results: [
        { metric: '计算精度', improvement: '误差<0.1%', icon: faChartLine },
        { metric: '成本分摊', improvement: '精准度100%', icon: faAward },
        { metric: '利润透明度', improvement: '实时可见', icon: faUsers },
        { metric: '决策支持', improvement: '数据化100%', icon: faClock }
      ],
      testimonial: {
        content: '现在每票货的成本和利润都一清二楚，经营决策有了可靠的数据支撑。',
        author: '赵经理',
        position: '网点经理'
      },
      tags: ['零担专线', '精准计算', '利润分析']
    },
    {
      id: 7,
      company: '融辉物流（武汉网点）',
      industry: '专线物流',
      icon: faPlane,
      logo: '/api/placeholder/120/60',
      description: '融辉物流武汉地区专线网点',
      challenge: '专线运输路线固定，但货量波动大，成本控制难度高',
      solution: '建立动态成本控制系统，实现路线优化和成本预警',
      results: [
        { metric: '成本控制', improvement: '精准度提升90%', icon: faChartLine },
        { metric: '路线优化', improvement: '效率提升60%', icon: faAward },
        { metric: '预警准确率', improvement: '达到95%', icon: faUsers },
        { metric: '利润率', improvement: '提升20%', icon: faClock }
      ],
      testimonial: {
        content: '系统的成本预警功能帮助我们及时调整策略，避免了很多潜在损失。',
        author: '孙总',
        position: '网点负责人'
      },
      tags: ['专线运输', '成本预警', '路线优化']
    },
    {
      id: 8,
      company: '速通物流（西安网点）',
      industry: '城配物流',
      icon: faShip,
      logo: '/api/placeholder/120/60',
      description: '速通物流西安地区城配网点',
      challenge: '城配业务点多面广，配送成本难以精确核算',
      solution: '部署GPS轨迹成本核算系统，实现配送成本精准计算',
      results: [
        { metric: '成本核算', improvement: '精度提升95%', icon: faChartLine },
        { metric: '配送效率', improvement: '提升45%', icon: faAward },
        { metric: '油耗管控', improvement: '降低15%', icon: faUsers },
        { metric: '客户满意度', improvement: '提升40%', icon: faClock }
      ],
      testimonial: {
        content: 'GPS轨迹成本核算让我们对每一单配送的真实成本都了如指掌，管理更加精细化。',
        author: '马经理',
        position: '运营经理'
      },
      tags: ['城配物流', 'GPS轨迹', '精细化管理']
    }
  ];

  const industryStats = [
    { label: '合作网点', value: '3000+', description: '个物流网点' },
    { label: '覆盖品牌', value: '8+', description: '大物流品牌' },
    { label: '服务城市', value: '500+', description: '个城市' },
    { label: '网点满意度', value: '96%', description: '综合评分' }
  ];

  useEffect(() => {
    // 页面入场动画
    gsap.fromTo(
      pageRef.current.children,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out'
      }
    );
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen bg-gray-50 page-transition">
      {/* 英雄区域 */}
      <section className="py-20 bg-gradient-hero text-white flex items-center min-h-[300px]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-2xl md:text-3xl font-medium text-blue-100">
            看看我们如何帮助物流网点实现财务管理数字化转型
          </p>
        </div>
      </section>

      {/* 行业数据统计 */}
      <div className="bg-blue-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industryStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-blue-100">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 客户案例选择器 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">成功案例</h2>
          <p className="text-xl text-gray-600">选择网点了解详细合作案例</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {customerCases.map((case_, index) => (
            <button
              key={case_.id}
              onClick={() => setSelectedCase(index)}
              className={`group p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${
                selectedCase === index
                  ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-xl scale-105'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className="flex flex-col items-center text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${
                  selectedCase === index
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg'
                    : 'bg-gradient-to-r from-gray-400 to-gray-500 group-hover:from-blue-400 group-hover:to-indigo-500'
                }`}>
                  <FontAwesomeIcon icon={case_.icon} className="text-white text-2xl" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">{case_.company}</h3>
                <p className="text-sm text-gray-600">{case_.industry}</p>
              </div>
            </button>
          ))}
        </div>

        {/* 选中案例详情 */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="p-8 lg:p-12">
            {/* 案例头部 */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div className="flex items-center mb-4 lg:mb-0">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-lg mr-6">
                  <FontAwesomeIcon icon={customerCases[selectedCase].icon} className="text-white text-3xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{customerCases[selectedCase].company}</h2>
                  <p className="text-lg text-gray-600">{customerCases[selectedCase].description}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {customerCases[selectedCase].tags.map((tag, index) => (
                  <span key={index} className="px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 text-sm rounded-full font-medium border border-blue-200">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* 案例内容 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* 左侧：挑战与解决方案 */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-lg" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">面临挑战</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{customerCases[selectedCase].challenge}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                      <FontAwesomeIcon icon={faLightbulb} className="text-green-600 text-lg" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">解决方案</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{customerCases[selectedCase].solution}</p>
                </div>
              </div>

              {/* 右侧：实施效果 */}
              <div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-8">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                      <FontAwesomeIcon icon={faChartLine} className="text-blue-600 text-lg" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">实施效果</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {customerCases[selectedCase].results.map((result, index) => (
                      <div key={index} className="text-center p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <FontAwesomeIcon icon={result.icon} className="text-white text-lg" />
                        </div>
                        <div className="text-sm text-gray-600 mb-2">{result.metric}</div>
                        <div className="font-bold text-gray-900 text-lg">{result.improvement}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 客户评价 */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <FontAwesomeIcon icon={faQuoteLeft} className="text-yellow-600 text-lg" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900 mb-3">客户评价</h4>
                      <p className="text-gray-700 italic leading-relaxed text-lg mb-4">
                        "{customerCases[selectedCase].testimonial.content}"
                      </p>
                      <div className="text-right">
                        <div className="font-bold text-gray-900">
                          {customerCases[selectedCase].testimonial.author}
                        </div>
                        <div className="text-gray-600">
                          {customerCases[selectedCase].testimonial.position}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA区域 */}
      <div className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">想了解更多成功案例？</h2>
          <p className="text-xl text-gray-300 mb-8">联系我们的专业顾问，获取详细的行业解决方案</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onPurchaseClick}
              className="bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              立即购买
            </button>
            <button 
              onClick={() => setIsDemoModalOpen(true)}
              className="border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-white hover:text-gray-900 transition-colors"
            >
              预约演示
            </button>
          </div>
        </div>
      </div>
      
      {/* Demo Modal - 只在没有onDemoClick prop时显示 */}
        {!onDemoClick && (
          <DemoModal
            isOpen={isDemoModalOpen}
            onClose={() => setIsDemoModalOpen(false)}
          />
        )}
    </div>
  );
};

export default Cases;